import type { UpdateTutorPayload } from '../api/update.hook'

import { useTutorKanbanStore } from './store'
import { useUpdateTutorApi } from '../api/update.hook'
import { useTutorKanbanSelectReason } from './tutor-kanban-select-reason.hook'
import { useTutorKanbanSelectCategory } from './tutor-kanban-select-category.hook'
import { useTutorKanbanExitConfirm } from './tutor-kanban-exit-confirm.hook'

interface TutorKanbanSaveReturn {
  setSubmitUpdate: () => Promise<void>
  setSubmitFinalDayConfirmed: () => Promise<void>
}

export const useTutorKanbanSave = (): TutorKanbanSaveReturn => {
  const tutorKanbanStore = useTutorKanbanStore()
  const reason = useTutorKanbanSelectReason()
  const category = useTutorKanbanSelectCategory()
  const { isFinalDayChanged, isFinalDayConfirmRequired } = useTutorKanbanExitConfirm()

  const isFormValid = (): boolean => {
    if (reason.isVisible.value && reason.refValue.value.length === 0) return false
    return !(category.isRequired.value && !category.refValue.value)
  }

  const submit = async (isFinalDayConfirmed: boolean): Promise<void> => {
    const tutor = tutorKanbanStore.getTutor
    if (!tutor) return

    if (reason.hasPendingChange.value) {
      reason.setConfirmRequest()
      return
    }

    if (!isFormValid()) {
      tutorKanbanStore.setIsValid(false)
      return
    }

    tutorKanbanStore.setIsValid(true)

    if (isFinalDayConfirmRequired.value && !isFinalDayConfirmed) {
      tutorKanbanStore.setFinalDayConfirmOpen(true)
      return
    }

    const hasSavedStageDetails = tutorKanbanStore.getSavedStageReasons.length > 0
      || tutorKanbanStore.getSavedStageCategories.length > 0

    const hasStageDetails = reason.isVisible.value || category.isVisible.value || hasSavedStageDetails

    const payload: UpdateTutorPayload = {
      stage: tutor.stage,
      kanban_priority: tutor.kanban_priority,
      stage_deadline: tutor.stage_deadline,
      agent_id: tutor.agent?.id ?? null,
      ...(hasStageDetails && {
        stage_details: {
          reasons: reason.refValue.value,
          categories: category.refValue.value ? [category.refValue.value] : [],
        },
      }),
      ...(isFinalDayChanged.value && {
        exit_date: tutor.exit_date,
      }),
    }

    await useUpdateTutorApi({
      tutorId: tutor.id,
      payload,
    })
  }

  const setSubmitUpdate = (): Promise<void> => submit(false)

  const setSubmitFinalDayConfirmed = (): Promise<void> => submit(true)

  return {
    setSubmitUpdate,
    setSubmitFinalDayConfirmed,
  }
}
